import { createContext, useCallback, useContext, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { post } from '../../api';
import {
    getStateByCode,
    getStateByName,
    setStateFeeOverrides,
    setEntityRenewalConfig,
    getEntityRenewalPrice,
    ONLINE_CART_FORM_PAYLOAD,
} from './dummyData';
import { getFields, validateScreen } from './validation';
import { getOnlineCartAnswerStorageKey, ONLINE_CART_ANSWER_ID_QUERY_PARAM } from './constants';
import { encryptOnlineCartRecordForService } from './ssnPayload';

const OnlineCartContext = createContext(null);

const ACTIONS = {
    SET_FIELD: 'SET_FIELD',
    SET_FIELD_ERRORS: 'SET_FIELD_ERRORS',
    REPLACE_ERRORS: 'REPLACE_ERRORS',
    HYDRATE_ANSWERS: 'HYDRATE_ANSWERS',
    SET_SECTIONS: 'SET_SECTIONS',
    NEXT_SCREEN: 'NEXT_SCREEN',
    PREV_SCREEN: 'PREV_SCREEN',
    SET_SCREEN_INDEX: 'SET_SCREEN_INDEX',
};

const DYNAMIC_PRODUCT_REPLACEMENT_TARGETS = new Set(['registered_agent', 'expedited_processing']);
const PRODUCT_SCREEN_ACCOUNT_SECTION_ID = 'account';

function dynamicProductFieldId(product) {
    const stableKey = product?.productCode || product?.id;
    return stableKey ? `cartProductSelection_${stableKey}` : '';
}

function mapHeroIconFromSf(iconName) {
    const value = String(iconName || '').toLowerCase();
    if (value === 'bolt' || value === 'lightning' || value === 'zap') return 'zap';
    return 'shield';
}

function mapOptionLeadingIconFromSf(iconName) {
    const value = String(iconName || '').toLowerCase();
    if (value === 'bolt' || value === 'lightning' || value === 'zap') return 'zap';
    if (value === 'clock' || value === 'time') return 'clock';
    return undefined;
}

// Mirror the configurator's icon menu (Cart_Product_Benefit__c.Icon_Name__c) so each configured
// benefit icon renders as itself instead of silently collapsing to a check.
const BENEFIT_ICON_NAMES = new Set([
    'shield', 'bolt', 'lock', 'bell', 'check', 'clock', 'info', 'warning', 'cart', 'star',
    // legacy names used by static (non-configurator) screens
    'wallet', 'card', 'trend',
]);

function mapBenefitIconFromSf(iconName) {
    const value = String(iconName || '').toLowerCase();
    return BENEFIT_ICON_NAMES.has(value) ? value : 'check';
}

function buildProductScreen(product) {
    if (!product || product.isActive === false) return null;

    const fieldId = dynamicProductFieldId(product);
    if (!fieldId) return null;

    const sortedOptions = [...(product.options || [])]
        .filter((opt) => opt && opt.isActive !== false)
        .sort((a, b) => (Number(a.displayOrder) || 0) - (Number(b.displayOrder) || 0));

    const sortedBenefits = [...(product.benefits || [])]
        .sort((a, b) => (Number(a.displayOrder) || 0) - (Number(b.displayOrder) || 0));

    const field = {
        id: fieldId,
        type: 'choiceCards',
        required: Boolean(product.isRequired),
        requiredMessage: 'Please select one option to continue.',
        ctaLabel: product.helpTitle || 'Why do I need this?',
        helpExpandedByDefault: Boolean(product.helpExpandedByDefault),
        options: sortedOptions.map((opt) => ({
            value: opt.optionCode || opt.id,
            title: opt.label || '',
            subtitle: opt.subtitle || '',
            priceLabel: opt.priceDisplay || '',
            recommended: Boolean(opt.isRecommended),
            priceColor: opt.isDeclineOption && Number(opt.price || 0) === 0 ? 'success' : undefined,
            leadingIcon: mapOptionLeadingIconFromSf(opt.iconName),
        })),
    };

    if (sortedBenefits.length > 0) {
        field.benefits = sortedBenefits.map((b) => ({
            icon: mapBenefitIconFromSf(b.iconName),
            text: b.benefitText || '',
        }));
    }

    if (product.footerNote) {
        field.note = {
            icon: 'info',
            title: 'Note:',
            text: product.footerNote,
        };
    }

    return {
        id: `cart_product_${product.productCode || product.id}`,
        heroIcon: mapHeroIconFromSf(product.iconName),
        title: product.title || '',
        description: product.description || '',
        fields: [field],
    };
}

function injectProductScreens(staticSections, products) {
    if (!Array.isArray(staticSections)) return staticSections || [];
    const activeProducts = Array.isArray(products)
        ? [...products]
              .filter((p) => p && p.isActive !== false)
              .sort((a, b) => (Number(a.displayOrder) || 0) - (Number(b.displayOrder) || 0))
        : [];

    if (activeProducts.length === 0) {
        return staticSections;
    }

    const dynamicScreens = activeProducts
        .map((p) => buildProductScreen(p))
        .filter(Boolean);

    if (dynamicScreens.length === 0) {
        return staticSections;
    }

    return staticSections.map((section) => {
        if (section?.id !== PRODUCT_SCREEN_ACCOUNT_SECTION_ID) return section;
        const screens = Array.isArray(section.screens) ? section.screens : [];

        const firstReplacementIndex = screens.findIndex((s) => DYNAMIC_PRODUCT_REPLACEMENT_TARGETS.has(s?.id));
        if (firstReplacementIndex === -1) return section;

        const withoutTargets = screens.filter((s) => !DYNAMIC_PRODUCT_REPLACEMENT_TARGETS.has(s?.id));
        const nextScreens = [
            ...withoutTargets.slice(0, firstReplacementIndex),
            ...dynamicScreens,
            ...withoutTargets.slice(firstReplacementIndex),
        ];

        return { ...section, screens: nextScreens };
    });
}

const RELAY_BANK_SCREEN_ID = 'relay_bank';

function applyCartDisplayFlags(sections, cart) {
    if (!cart) return sections;
    // displayRelayAccount default is true; explicit false hides the screen.
    if (cart.displayRelayAccount === false) {
        return sections.map((section) => {
            if (!Array.isArray(section?.screens)) return section;
            const filtered = section.screens.filter((s) => s?.id !== RELAY_BANK_SCREEN_ID);
            if (filtered.length === section.screens.length) return section;
            return { ...section, screens: filtered };
        });
    }
    return sections;
}

function computeProductAnswerDefaults(products) {
    const defaults = {};
    if (!Array.isArray(products)) return defaults;
    for (const product of products) {
        if (!product || product.isActive === false) continue;
        const fieldId = dynamicProductFieldId(product);
        if (!fieldId) continue;
        const defaultOption = (product.options || []).find((o) => o && o.isDefaultSelected && o.isActive !== false);
        if (defaultOption) {
            defaults[fieldId] = defaultOption.optionCode || defaultOption.id;
        }
    }
    return defaults;
}
const STATE_OF_FORMATION_FIELD_ID = 'stateOfFormation';
const MAILING_STATE_FIELD_ID = 'mailingState';
const MAILING_ADDRESS_FIELD_IDS = new Set([
    'mailingAddress1',
    'mailingAddress2',
    'mailingCity',
    'mailingState',
    'mailingStateCode',
    'mailingZip',
]);
const ENABLE_ONLINE_CART_AUTOSAVE = false;
const ONLINE_CART_RECORD_CACHE = new Map();
const ONLINE_CART_RECORD_REQUEST_CACHE = new Map();

function flattenScreens(sections = []) {
    if (!Array.isArray(sections)) {
        return [];
    }

    return sections.flatMap((section) => {
        if (!Array.isArray(section?.screens)) {
            return [];
        }

        return section.screens.map((screen) => ({
            ...screen,
            sectionId: section.id,
            sectionLabel: section.label,
        }));
    });
}

function clampScreenIndex(index, totalScreens) {
    if (totalScreens <= 0) {
        return -1;
    }
    return Math.max(0, Math.min(totalScreens - 1, index));
}

function initialStateFromPayload({ payload, recordId = '' }) {
    const sections = payload?.sections || [];
    const flattenedScreens = flattenScreens(sections);
    const stepFromResponse = payload?.response?.progress?.currentStep || 1; // response index start from 1
    const currentScreenIndex = clampScreenIndex(stepFromResponse - 1, flattenedScreens.length); // start from 0

    const initialAnswers = payload?.response?.answers || {};
    const hydratedAnswers = hydrateAnswersWithStateSnapshot(initialAnswers);

    return {
        sections,
        flattenedScreens,
        currentScreenIndex,
        recordId,
        answers: hydratedAnswers,
        errors: {},
    };
}

const CART_PRODUCT_SELECTION_ARRAY_KEY = 'cartProductSelection';
const CART_PRODUCT_SELECTION_FLAT_PREFIX = 'cartProductSelection_';

function expandCartProductSelectionArray(answers = {}) {
    const next = { ...(answers || {}) };
    const arr = next[CART_PRODUCT_SELECTION_ARRAY_KEY];
    if (Array.isArray(arr)) {
        for (const item of arr) {
            const cartProductId = item?.cartProductId;
            if (!cartProductId) continue;
            const flatKey = `${CART_PRODUCT_SELECTION_FLAT_PREFIX}${cartProductId}`;
            // Only set if a flat key isn't already present (let later edits win)
            if (next[flatKey] === undefined) {
                next[flatKey] = item.selectedOptionId || '';
            }
        }
    }
    delete next[CART_PRODUCT_SELECTION_ARRAY_KEY];
    return next;
}

function collapseCartProductSelectionToArray(answers = {}) {
    const next = { ...(answers || {}) };
    const selections = [];
    for (const key of Object.keys(next)) {
        if (!key.startsWith(CART_PRODUCT_SELECTION_FLAT_PREFIX)) continue;
        const cartProductId = key.slice(CART_PRODUCT_SELECTION_FLAT_PREFIX.length);
        const selectedOptionId = next[key];
        if (cartProductId && selectedOptionId) {
            selections.push({ cartProductId, selectedOptionId });
        }
        delete next[key];
    }
    if (selections.length > 0) {
        next[CART_PRODUCT_SELECTION_ARRAY_KEY] = selections;
    } else {
        delete next[CART_PRODUCT_SELECTION_ARRAY_KEY];
    }
    return next;
}

function hydrateAnswersWithStateSnapshot(answers = {}) {
    const nextAnswers = expandCartProductSelectionArray(answers);
    const selectedFormation =
        typeof nextAnswers.stateOfFormation === 'object' && nextAnswers.stateOfFormation
            ? getStateByCode(nextAnswers.stateOfFormation.stateCode)
            : getStateByCode(nextAnswers.stateCode || nextAnswers.stateOfFormation);
    if (selectedFormation) {
        nextAnswers.stateOfFormation = {
            stateCode: selectedFormation.code,
            stateName: nextAnswers?.stateOfFormation?.stateName || nextAnswers?.stateName || selectedFormation.name,
            stateFee: Number.isFinite(Number(nextAnswers?.stateOfFormation?.stateFee))
                ? Number(nextAnswers.stateOfFormation.stateFee)
                : Number.isFinite(Number(nextAnswers?.stateFee))
                  ? Number(nextAnswers.stateFee)
                  : selectedFormation.fee,
        };
    }
    delete nextAnswers.stateCode;
    delete nextAnswers.stateName;
    delete nextAnswers.stateFee;

    const selectedMailing = getStateByName(answers?.mailingStateName || answers?.mailingState);
    if (selectedMailing) {
        nextAnswers.mailingState = selectedMailing.name;
        nextAnswers.mailingStateCode = selectedMailing.code;
    }
    delete nextAnswers.mailingStateName;

    return nextAnswers;
}

function reducer(state, action) {
    switch (action.type) {
        case ACTIONS.SET_FIELD: {
            const { fieldId, value } = action.payload;
            const isStateOfFormationChange = fieldId === STATE_OF_FORMATION_FIELD_ID;
            const isMailingStateChange = fieldId === MAILING_STATE_FIELD_ID;
            const isMailingAddressFieldChange = MAILING_ADDRESS_FIELD_IDS.has(fieldId);
            const selectedFormation = isStateOfFormationChange ? getStateByCode(String(value || '')) : null;
            const selectedMailing = isMailingStateChange ? getStateByName(String(value || '')) : null;

            return {
                ...state,
                answers: {
                    ...state.answers,
                    [fieldId]: value,
                    ...(isStateOfFormationChange
                        ? {
                              stateOfFormation: selectedFormation
                                  ? {
                                        stateCode: selectedFormation.code,
                                        stateName: selectedFormation.name,
                                        stateFee: selectedFormation.fee,
                                    }
                                  : null,
                          }
                        : {}),
                    ...(isMailingStateChange
                        ? {
                              mailingState: selectedMailing?.name || String(value || ''),
                              mailingStateCode: selectedMailing?.code || '',
                          }
                        : {}),
                    // Editing the address invalidates a prior "proceed with this address" override,
                    // so the next attempt re-runs USPS validation instead of passing on the stale flag.
                    ...(isMailingAddressFieldChange ? { mailingAddressOverride: null } : {}),
                },
                errors: {
                    ...state.errors,
                    [fieldId]: '',
                    // Editing any mailing address field dismisses the address-validation
                    // message + "proceed with this address" offer so the next attempt re-checks fresh.
                    ...(isMailingAddressFieldChange ? { mailingAddress: '' } : {}),
                },
            };
        }
        case ACTIONS.SET_FIELD_ERRORS: {
            return {
                ...state,
                errors: {
                    ...state.errors,
                    ...action.payload,
                },
            };
        }
        case ACTIONS.REPLACE_ERRORS: {
            return {
                ...state,
                errors: action.payload,
            };
        }
        case ACTIONS.HYDRATE_ANSWERS: {
            const incomingAnswers = action.payload?.answers || {};
            const progress = action.payload?.progress || {};
            const currentStep = Number(progress?.currentStep) || 1;
            const hydratedAnswers = hydrateAnswersWithStateSnapshot(incomingAnswers);

            return {
                ...state,
                answers: hydratedAnswers,
                currentScreenIndex: clampScreenIndex(currentStep - 1, state.flattenedScreens.length),
                errors: {},
            };
        }
        case ACTIONS.SET_SECTIONS: {
            const nextSections = Array.isArray(action.payload?.sections) ? action.payload.sections : [];
            const nextFlattenedScreens = flattenScreens(nextSections);

            const currentScreen = state.flattenedScreens[state.currentScreenIndex];
            let nextIndex = state.currentScreenIndex;
            if (currentScreen?.id) {
                const found = nextFlattenedScreens.findIndex((s) => s.id === currentScreen.id);
                if (found !== -1) {
                    nextIndex = found;
                }
            }

            return {
                ...state,
                sections: nextSections,
                flattenedScreens: nextFlattenedScreens,
                currentScreenIndex: clampScreenIndex(nextIndex, nextFlattenedScreens.length),
            };
        }
        case ACTIONS.NEXT_SCREEN: {
            return {
                ...state,
                currentScreenIndex: clampScreenIndex(state.currentScreenIndex + 1, state.flattenedScreens.length),
            };
        }
        case ACTIONS.PREV_SCREEN: {
            return {
                ...state,
                currentScreenIndex: clampScreenIndex(state.currentScreenIndex - 1, state.flattenedScreens.length),
            };
        }
        case ACTIONS.SET_SCREEN_INDEX: {
            return {
                ...state,
                currentScreenIndex: clampScreenIndex(action.payload, state.flattenedScreens.length),
            };
        }
        default:
            return state;
    }
}

export function OnlineCartProvider({ children, recordId = '', initialAnswerId = '' }) {
    const [state, dispatch] = useReducer(reducer, { payload: ONLINE_CART_FORM_PAYLOAD, recordId }, initialStateFromPayload);
    const [onlineCartRecord, setOnlineCartRecord] = useState(null);
    const [onlineCartRecordLoading, setOnlineCartRecordLoading] = useState(Boolean(recordId));
    const [onlineCartRecordError, setOnlineCartRecordError] = useState('');
    const [answerId, setAnswerId] = useState(initialAnswerId || '');
    const answerIdRef = useRef(initialAnswerId || '');
    const initialLoadAnswerIdRef = useRef(initialAnswerId || '');
    const hasCompletedInitialLoadRef = useRef(false);
    const didHydrateAnswersRef = useRef(false);
    const saveDebounceRef = useRef(null);
    const lastSavedAnswersRef = useRef(JSON.stringify(state.answers || {}));
    const currentStepRef = useRef(state.currentScreenIndex + 1);
    const currentScreen = state.flattenedScreens[state.currentScreenIndex];
    const currentFields = getFields(currentScreen);
    // const canProceedCurrentScreen = useMemo(() => currentFields.every((field) => !validateField(field, state.answers)), [currentFields, state.answers]);

    const persistAnswerId = useCallback(
        (nextAnswerId) => {
            if (!recordId || !nextAnswerId) {
                return;
            }

            try {
                window.localStorage.setItem(getOnlineCartAnswerStorageKey(recordId), nextAnswerId);
            } catch (error) {
                // Ignore storage failures for private browsing or restricted environments.
            }

            try {
                const nextUrl = new URL(window.location.href);
                nextUrl.searchParams.set(ONLINE_CART_ANSWER_ID_QUERY_PARAM, nextAnswerId);
                window.history.replaceState(window.history.state, '', `${nextUrl.pathname}${nextUrl.search}${nextUrl.hash}`);
            } catch (error) {
                // Ignore URL mutation failures when browser APIs are unavailable.
            }
        },
        [recordId]
    );

    const saveAnswers = useCallback(
        async ({ answers = state.answers || {}, progress } = {}) => {
            if (!recordId) {
                throw new Error('Unable to persist answers without a cart id.');
            }

            // const serializedAnswers = JSON.stringify(state.answers || {});
            // if (serializedAnswers === lastSavedAnswersRef.current) {
            //     return answerIdRef.current ;
            // }

            if (saveDebounceRef.current) {
                clearTimeout(saveDebounceRef.current);
                saveDebounceRef.current = null;
            }

            const nextAnswers = answers && typeof answers === 'object' ? answers : {};
            const serializableAnswers = collapseCartProductSelectionToArray(nextAnswers);
            const nextProgress =
                progress && typeof progress === 'object'
                    ? progress
                    : {
                          currentStep: currentStepRef.current,
                      };
            const activeAnswerId = answerIdRef.current || '';

            const recordForService = await encryptOnlineCartRecordForService({
                answers: serializableAnswers,
                progress: nextProgress,
            });

            const result = await post('onlineCart/saveAnswer', {
                record: recordForService,
                cartId: recordId,
                id: activeAnswerId,
            });

            if (!result?.isSuccess) {
                throw new Error(result?.errorMessage || 'Unable to persist answers.');
            }

            const savedAnswerId = result?.data?.id || activeAnswerId || '';
            if (!savedAnswerId) {
                throw new Error('Unable to determine answerId for secure operations.');
            }

            answerIdRef.current = savedAnswerId;
            setAnswerId(savedAnswerId);
            persistAnswerId(savedAnswerId);
            lastSavedAnswersRef.current = JSON.stringify(nextAnswers);

            return {
                ...(result?.data || {}),
                id: savedAnswerId,
            };
        },
        [persistAnswerId, recordId, state.answers]
    );

    const setField = useCallback((fieldId, fieldValue) => dispatch({ type: ACTIONS.SET_FIELD, payload: { fieldId, value: fieldValue } }), [])

    useEffect(() => {
        currentStepRef.current = state.currentScreenIndex + 1;
    }, [state.currentScreenIndex]);

    useEffect(() => {
        answerIdRef.current = answerId || '';
    }, [answerId]);

    useEffect(() => {
        let active = true;

        const hydrateFromRecord = (nextRecord, fallbackAnswerId = '') => {
            setOnlineCartRecord(nextRecord || null);
            // Apply cart-configured pricing (state fees + entity annual renewal) so the rest of
            // the flow reads from configuration rather than the hardcoded defaults.
            setStateFeeOverrides(nextRecord?.stateFees);
            setEntityRenewalConfig(
                nextRecord?.entityAnnualRenewalPrice,
                nextRecord?.entityAnnualRenewalLabel
            );
            const resolvedAnswerId = nextRecord?.savedAnswer?.id || nextRecord?.answerId || fallbackAnswerId || '';

            if (resolvedAnswerId) {
                answerIdRef.current = resolvedAnswerId;
                setAnswerId(resolvedAnswerId);
                persistAnswerId(resolvedAnswerId);
            }

            const products = Array.isArray(nextRecord?.products) ? nextRecord.products : [];
            let nextSections = ONLINE_CART_FORM_PAYLOAD.sections;
            if (products.length > 0) {
                nextSections = injectProductScreens(nextSections, products);
            }
            // Hide relay_bank screen when the cart's Display_Relay_Account__c flag is explicitly false.
            nextSections = applyCartDisplayFlags(nextSections, nextRecord);
            if (nextSections !== ONLINE_CART_FORM_PAYLOAD.sections) {
                dispatch({ type: ACTIONS.SET_SECTIONS, payload: { sections: nextSections } });
            }

            const productDefaults = computeProductAnswerDefaults(products);
            const savedAnswers = nextRecord?.savedAnswer?.answers;
            const savedProgress = nextRecord?.savedAnswer?.progress;

            const hasSavedAnswers = savedAnswers && typeof savedAnswers === 'object';
            const hasProductDefaults = Object.keys(productDefaults).length > 0;
            // The configured entity-annual-renewal price must travel with the answers payload
            // so Salesforce charges the configured amount instead of a hardcoded default.
            const configuredRenewalPrice = getEntityRenewalPrice();

            if (hasSavedAnswers || hasProductDefaults || configuredRenewalPrice != null) {
                const mergedAnswers = { ...productDefaults, ...(hasSavedAnswers ? savedAnswers : {}) };
                mergedAnswers.entityAnnualRenewalPrice = configuredRenewalPrice;
                didHydrateAnswersRef.current = true;
                lastSavedAnswersRef.current = JSON.stringify(hydrateAnswersWithStateSnapshot(mergedAnswers));
                dispatch({
                    type: ACTIONS.HYDRATE_ANSWERS,
                    payload: {
                        answers: mergedAnswers,
                        progress: savedProgress,
                    },
                });
            }
        };

        const loadOnlineCartRecord = async () => {
            if (!recordId) {
                if (active) {
                    setOnlineCartRecord(null);
                    setOnlineCartRecordLoading(false);
                    setOnlineCartRecordError('');
                    hasCompletedInitialLoadRef.current = true;
                }
                return;
            }

            const requestedAnswerId = initialLoadAnswerIdRef.current || '';
            const cacheKey = `${recordId}:${requestedAnswerId}`;
            if (ONLINE_CART_RECORD_CACHE.has(cacheKey)) {
                const cached = ONLINE_CART_RECORD_CACHE.get(cacheKey);
                if (active) {
                    setOnlineCartRecordError(cached?.error || '');
                    setOnlineCartRecordLoading(false);
                    hydrateFromRecord(cached?.record || null, requestedAnswerId);
                    hasCompletedInitialLoadRef.current = true;
                }
                return;
            }

            setOnlineCartRecordLoading(true);
            setOnlineCartRecordError('');

            try {
                let requestPromise = ONLINE_CART_RECORD_REQUEST_CACHE.get(cacheKey);
                if (!requestPromise) {
                    requestPromise = post('onlineCart/getOnlineCart', { recordId, answerId: requestedAnswerId });
                    ONLINE_CART_RECORD_REQUEST_CACHE.set(cacheKey, requestPromise);
                }

                const result = await requestPromise;
                ONLINE_CART_RECORD_REQUEST_CACHE.delete(cacheKey);
                if (!active) {
                    return;
                }

                if (result?.isSuccess) {
                    const nextRecord = result?.data || null;
                    ONLINE_CART_RECORD_CACHE.set(cacheKey, { record: nextRecord, error: '' });
                    hydrateFromRecord(nextRecord, requestedAnswerId);
                    return;
                }

                const errorMessage = result?.errorMessage || 'Unable to load online cart record.';
                ONLINE_CART_RECORD_CACHE.set(cacheKey, { record: null, error: errorMessage });
                setOnlineCartRecord(null);
                setOnlineCartRecordError(errorMessage);
            } catch (error) {
                if (!active) {
                    return;
                }

                ONLINE_CART_RECORD_REQUEST_CACHE.delete(cacheKey);

                const errorMessage = error?.message || 'Unable to load online cart record.';
                ONLINE_CART_RECORD_CACHE.set(cacheKey, { record: null, error: errorMessage });
                setOnlineCartRecord(null);
                setOnlineCartRecordError(errorMessage);
            } finally {
                if (active) {
                    setOnlineCartRecordLoading(false);
                    hasCompletedInitialLoadRef.current = true;
                }
            }
        };

        loadOnlineCartRecord();

        return () => {
            active = false;
        };
    }, [persistAnswerId, recordId]);

    useEffect(() => {
        if (!ENABLE_ONLINE_CART_AUTOSAVE) {
            return;
        }

        if (!recordId || !hasCompletedInitialLoadRef.current) {
            return;
        }

        if (didHydrateAnswersRef.current) {
            didHydrateAnswersRef.current = false;
            return;
        }

        const serializedAnswers = JSON.stringify(state.answers || {});
        if (serializedAnswers === lastSavedAnswersRef.current) {
            return;
        }

        if (saveDebounceRef.current) {
            clearTimeout(saveDebounceRef.current);
        }

        saveDebounceRef.current = setTimeout(async () => {
            // const { isValid, firstErrorFieldId } = validateCurrentScreen({shouldDispatch: false});
            // if(!isValid){
            //     return
            // }
            // Use one out of these to validate first
            // if(!canProceedCurrentScreen){
            //     return
            // }
            try {
                await saveAnswers({
                    answers: state.answers || {},
                    progress: {
                        currentStep: currentStepRef.current,
                    },
                });
            } catch (error) {
                // Best-effort autosave; retain local changes for retry on next edit.
            }
        }, 1000);

        return () => {
            if (saveDebounceRef.current) {
                clearTimeout(saveDebounceRef.current);
            }
        };
    }, [recordId, saveAnswers, state.answers]);

    const sectionProgress = useMemo(
        () =>
            (Array.isArray(state.sections) ? state.sections : []).map((section, index) => ({
                label: section?.label || `Section ${index + 1}`,
                stepCount: Array.isArray(section?.screens) ? section.screens.length : 0,
            })),
        [state.sections]
    );

    const validateCurrentScreen = (options = { shouldDispatch: true }) => {
        const mergedErrors = { ...state.errors };
        const currentFieldIds = currentFields.map((field) => field.id);
        const currentScreenErrors = validateScreen(currentScreen, state.answers);

        currentFieldIds.forEach((fieldId) => {
            delete mergedErrors[fieldId];
        });

        Object.assign(mergedErrors, currentScreenErrors);

        options.shouldDispatch && dispatch({ type: ACTIONS.REPLACE_ERRORS, payload: mergedErrors });

        const firstErrorFieldId = currentFieldIds.find((fieldId) => Boolean(mergedErrors[fieldId])) || '';
        const isValid = !firstErrorFieldId;

        return {
            isValid,
            firstErrorFieldId,
        };
    };

    console.log(state)

    const value = {
        state,
        currentScreen,
        currentFields,
        recordId: state.recordId,
        answerId,
        onlineCartRecord,
        onlineCartRecordLoading,
        onlineCartRecordError,
        hasFormData: state.flattenedScreens.length > 0,
        // canProceedCurrentScreen,
        currentStep: state.currentScreenIndex + 1,
        totalSteps: state.flattenedScreens.length,
        sectionProgress,
        setField,
        setFieldErrors: (errors) => dispatch({ type: ACTIONS.SET_FIELD_ERRORS, payload: errors }),
        saveAnswers,
        advanceNextScreen: () => {
            const { isValid, firstErrorFieldId } = validateCurrentScreen();
            if (isValid) {
                dispatch({ type: ACTIONS.NEXT_SCREEN });
            }
            return {
                isValid,
                firstErrorFieldId,
            };
        },
        validateCurrentScreen,
        prevScreen: () => dispatch({ type: ACTIONS.PREV_SCREEN }),
        nextScreen: () => dispatch({ type: ACTIONS.NEXT_SCREEN }),
        goToStep: (step) => dispatch({ type: ACTIONS.SET_SCREEN_INDEX, payload: step - 1 }),
    };

    return <OnlineCartContext.Provider value={value}>{children}</OnlineCartContext.Provider>;
}

export function useOnlineCart() {
    const context = useContext(OnlineCartContext);
    if (!context) {
        throw new Error('useOnlineCart must be used within OnlineCartProvider');
    }
    return context;
}
